import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Star,
  CheckCircle,
  Insights,
  Notifications,
  Psychology,
  Email,
  People,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";

const WelcomePage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          {t("welcome.title")}
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: "800px", mx: "auto" }}
        >
          {t("welcome.subtitle")}
        </Typography>

        {/* Main Action Cards */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          sx={{ mb: 8 }}
          alignItems="stretch"
        >
          <Box sx={{ width: "100%", flex: 1 }}>
            <Card
              sx={{
                height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <TrendingDown
                  sx={{ fontSize: 60, color: "error.main", mb: 2 }}
                />
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {t("welcome.breakBadHabits.title")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t("welcome.breakBadHabits.description")}
                </Typography>
                <Button
                  component={Link}
                  to={user ? "/habits" : "/auth/signup"}
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<TrendingDown />}
                  sx={{ textDecoration: "none" }}
                >
                  {t("welcome.breakBadHabits.button")}
                </Button>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ width: "100%", flex: 1 }}>
            <Card
              sx={{
                height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <TrendingUp
                  sx={{ fontSize: 60, color: "success.main", mb: 2 }}
                />
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {t("welcome.buildNewHabits.title")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t("welcome.buildNewHabits.description")}
                </Typography>
                <Button
                  component={Link}
                  to={user ? "/habits" : "/auth/signup"}
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<TrendingUp />}
                  sx={{ textDecoration: "none" }}
                >
                  {t("welcome.buildNewHabits.button")}
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ mb: 8 }} />

      {/* Features Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          {t("welcome.features.title")}
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          {t("welcome.features.subtitle")}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="stretch"
        >
          {/* Free Tier */}
          <Box sx={{ width: "100%", flex: 1 }}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                height: "100%",
                position: "relative",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {t("welcome.pricing.free.title")}
                </Typography>
                <Typography
                  variant="h2"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  {t("welcome.pricing.free.price")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("welcome.pricing.free.period")}
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.free.features.manualTracking")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.free.features.basicTracking")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.free.features.simpleInterface")}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 4 }}>
                <Button
                  component={Link}
                  to={!user ? "/auth/signup" : "/habits"}
                  fullWidth
                  variant="outlined"
                  size="large"
                  sx={{ textDecoration: "none" }}
                >
                  {user
                    ? t("welcome.pricing.free.buttonAuth")
                    : t("welcome.pricing.free.button")}
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Plus Tier */}
          <Box sx={{ width: "100%", flex: 1 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                height: "100%",
                position: "relative",
                border: "2px solid",
                borderColor: "primary.main",
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              }}
            >
              <Chip
                label={t("welcome.pricing.plus.popular")}
                color="primary"
                sx={{
                  position: "absolute",
                  top: -10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: "bold",
                }}
              />

              <Box sx={{ textAlign: "center", mb: 3, mt: 2 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {t("welcome.pricing.plus.title")}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="center"
                >
                  <Typography
                    variant="h2"
                    color="primary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("welcome.pricing.plus.price")}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {t("welcome.pricing.plus.period")}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("welcome.pricing.plus.subtitle")}
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.plus.features.freeFeatures")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Insights color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.plus.features.analytics")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Notifications color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.plus.features.emailReminders")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Psychology color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.plus.features.aiAdvice")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("welcome.pricing.plus.features.newsletter")}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 4 }}>
                <Button
                  component={Link}
                  to={user ? "/upgrade-plan" : "/auth/signup"}
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ fontWeight: "bold", textDecoration: "none" }}
                >
                  {user
                    ? t("welcome.pricing.plus.button")
                    : t("welcome.pricing.plus.buttonNotAuth")}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Stack>

        {/* Family Tier Info */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {t("welcome.pricing.family.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t("welcome.pricing.family.description")}
          </Typography>
          <Button
            component={Link}
            to="/pricing"
            variant="text"
            startIcon={<People />}
            sx={{ textDecoration: "none" }}
          >
            {t("welcome.pricing.family.button")}
          </Button>
        </Box>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          px: 4,
          backgroundColor: "primary.main",
          color: "white",
          borderRadius: 2,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
          {t("welcome.cta.title")}
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          {t("welcome.cta.subtitle")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          <Button
            component={Link}
            to={user ? "/habits" : "/auth/signup"}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "white",
              color: "primary.main",
              fontWeight: "bold",
              textDecoration: "none",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
            startIcon={<Star />}
          >
            {user
              ? t("welcome.cta.mainButton")
              : t("welcome.cta.mainButtonNotAuth")}
          </Button>
          <Button
            component={Link}
            to="/pricing"
            variant="outlined"
            size="large"
            sx={{
              borderColor: "white",
              color: "white",
              textDecoration: "none",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            {t("welcome.cta.secondaryButton")}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default WelcomePage;
